import {
  CreateAssessment,
  Assessment,
  AssessmentId,
  RiskMin,
  FindingMin,
  FilterAssessments,
  OwnerId,
  AssessmentStatus,
  TprmReportTemplateId,
  CreateRisk,
  Risk,
  CreateFinding,
  FindingId,
  AssessmentNode,
  CreateAssessmentDecision,
  ThirdPartyId,
  AssessmentControlId,
  PortalReportSnapshotId,
} from "platformed-client/types";
import {invalidateQueries, q} from "../../state";
import {refreshLinks} from "./tests";
import {platformedClient} from "..";

export async function search(filter: FilterAssessments): Promise<Assessment[]> {
  return await platformedClient.internalRequest("post", "/region/{region}/api/tprm/assessments/search", {body: filter});
}

export async function get(assessmentId: AssessmentId): Promise<Assessment> {
  return await platformedClient.internalRequest("get", "/region/{region}/api/tprm/assessments/{assessment_id}", {
    path: {assessment_id: assessmentId},
  });
}

export async function delete_(assessmentId: AssessmentId): Promise<void> {
  await platformedClient.internalRequest("delete", "/region/{region}/api/tprm/assessments/{assessment_id}", {
    path: {assessment_id: assessmentId},
  });
  await Promise.all([
    invalidateQueries([q.tprm.engagementAssessments.filter(), q.tprm.assessment.filter(assessmentId)]),
    refreshLinks(),
  ]);
}

export async function create(
  /**
   * The third party ID is not strictly needed to create the assessment, which
   * is why it's not in `CreateAssessment`. However, we provide it here so we can
   * invalidate some third party queries.
   */
  thirdPartyId: ThirdPartyId,
  assessment: CreateAssessment,
): Promise<Assessment> {
  const res = await platformedClient.internalRequest("post", "/region/{region}/api/tprm/assessments", {
    body: assessment,
  });
  await invalidateQueries([
    // Third party (`num_assessments` field)
    q.tprm.thirdParties.filter(),
    q.tprm.thirdParty.filter(thirdPartyId),
    // Data room (`num_assessments` field)
    q.tprm.thirdPartyEngagements.filter(),
    q.tprm.engagement.filter(assessment.engagement_id),
    // Assessments
    q.tprm.assessments.filter(),
    q.tprm.engagementAssessments.filter(assessment.engagement_id),
  ]);
  return res;
}

export async function continueAssessment(assessmentId: AssessmentId, autoAccept: boolean) {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/assessments/{assessment_id}/continue",
    {path: {assessment_id: assessmentId}, body: autoAccept},
  );
  await invalidateQueries([
    q.tprm.engagementAssessments.filter(),
    q.tprm.assessment.filter(),
    q.tprm.assessments.filter(),
    q.tprm.engagementAuditPlan.filter(),
  ]);
  return res;
}

export async function startAgentSession(assessmentId: AssessmentId) {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/assessments/{assessment_id}/agent_session",
    {path: {assessment_id: assessmentId}},
  );
  await invalidateQueries([
    q.tprm.engagementAssessments.filter(),
    q.tprm.assessment.filter(),
    q.tprm.assessments.filter(),
    q.tprm.engagementAuditPlan.filter(),
  ]);
  return res;
}

export async function createRisk(assessmentId: AssessmentId, risk: CreateRisk): Promise<Risk> {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/assessments/{assessment_id}/risks",
    {path: {assessment_id: assessmentId}, body: risk},
  );
  await invalidateQueries([
    q.tprm.assessmentRisks.filter(assessmentId),
    q.tprm.assessmentNodes.filter(assessmentId),
    q.tprm.engagementAuditPlan.filter(),
  ]);
  return res;
}

export async function listRisks(assessmentId: AssessmentId): Promise<RiskMin[]> {
  return await platformedClient.internalRequest("get", "/region/{region}/api/tprm/assessments/{assessment_id}/risks", {
    path: {assessment_id: assessmentId},
  });
}

export async function createFinding(assessmentId: AssessmentId, finding: CreateFinding): Promise<FindingId> {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/assessments/{assessment_id}/findings",
    {path: {assessment_id: assessmentId}, body: finding},
  );
  await invalidateQueries([
    q.tprm.assessmentFindings.filter(assessmentId),
    q.tprm.assessmentNodes.filter(assessmentId),
  ]);
  return res;
}

export async function listFindings(assessmentId: AssessmentId): Promise<FindingMin[]> {
  return await platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/assessments/{assessment_id}/findings",
    {path: {assessment_id: assessmentId}},
  );
}

export async function assign(assessmentId: AssessmentId, owner: OwnerId | null) {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/assessments/{assessment_id}/owner",
    {path: {assessment_id: assessmentId}, body: owner},
  );
  await invalidateQueries([
    q.tprm.engagementAssessments.filter(),
    q.tprm.assessments.filter(),
    q.tprm.assessment.filter(assessmentId),
  ]);
  return res;
}

export async function updateStatus(assessmentId: AssessmentId, status: AssessmentStatus) {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/assessments/{assessment_id}/status",
    {path: {assessment_id: assessmentId}, body: status},
  );
  await invalidateQueries([
    q.tprm.engagementAssessments.filter(),
    q.tprm.assessments.filter(),
    q.tprm.assessment.filter(assessmentId),
  ]);
  return res;
}

export async function listNodes(assessmentId: AssessmentId): Promise<AssessmentNode[]> {
  return await platformedClient.internalRequest("get", "/region/{region}/api/tprm/assessments/{assessment_id}/nodes", {
    path: {assessment_id: assessmentId},
  });
}

export async function listAssessmentControlIds(assessmentId: AssessmentId): Promise<AssessmentControlId[]> {
  return await platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/assessments/{assessment_id}/assessment_control_ids",
    {path: {assessment_id: assessmentId}},
  );
}

export async function bulkDecide(assessmentId: AssessmentId, decisions: CreateAssessmentDecision[]) {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/assessments/{assessment_id}/decisions",
    {path: {assessment_id: assessmentId}, body: decisions},
  );
  await invalidateQueries([
    q.tprm.assessment.filter(assessmentId),
    q.tprm.assessmentRisks.filter(assessmentId),
    q.tprm.assessmentNodes.filter(assessmentId),
    q.tprm.assessedControl.filter(assessmentId),
    q.tprm.risk.filter(assessmentId),
    q.tprm.assessmentFindings.filter(assessmentId),
    q.tprm.finding.filter(assessmentId),
  ]);
  return res;
}

export const downloadReport = async (assessmentId: AssessmentId, reportTemplateId: TprmReportTemplateId) => {
  const a = document.createElement("a");
  a.href = platformedClient.internalUrl(
    "get",
    "/region/{region}/api/tprm/assessments/{assessment_id}/report/{report_template_id}/download",
    {path: {assessment_id: assessmentId, report_template_id: reportTemplateId}},
  );
  a.click();
};

export const getReportDocxUrl = (assessmentId: AssessmentId, reportTemplateId: TprmReportTemplateId) => {
  return platformedClient.internalUrl(
    "get",
    "/region/{region}/api/tprm/assessments/{assessment_id}/report/{report_template_id}/download_docx",
    {path: {assessment_id: assessmentId, report_template_id: reportTemplateId}},
  );
};

export const downloadReportDocx = async (assessmentId: AssessmentId, reportTemplateId: TprmReportTemplateId) => {
  const a = document.createElement("a");
  a.href = getReportDocxUrl(assessmentId, reportTemplateId);
  a.click();
};

export const getReportXlsxUrl = (assessmentId: AssessmentId, reportTemplateId: TprmReportTemplateId) => {
  return platformedClient.internalUrl(
    "get",
    "/region/{region}/api/tprm/assessments/{assessment_id}/report/{report_template_id}/download_xlsx",
    {path: {assessment_id: assessmentId, report_template_id: reportTemplateId}},
  );
};

export const downloadReportXlsx = async (assessmentId: AssessmentId, reportTemplateId: TprmReportTemplateId) => {
  const a = document.createElement("a");
  a.href = getReportXlsxUrl(assessmentId, reportTemplateId);
  a.click();
};

export const getReportUrl = (assessmentId: AssessmentId, reportTemplateId: TprmReportTemplateId) => {
  return platformedClient.internalUrl(
    "get",
    "/region/{region}/api/tprm/assessments/{assessment_id}/report/{report_template_id}",
    {path: {assessment_id: assessmentId, report_template_id: reportTemplateId}},
  );
};

export async function publishToPortal(assessmentId: AssessmentId, reportTemplateId: TprmReportTemplateId) {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/assessments/{assessment_id}/report/{report_template_id}/publish",
    {path: {assessment_id: assessmentId, report_template_id: reportTemplateId}},
  );
  await invalidateQueries([q.tprm.reportSnapshots.filter(assessmentId, reportTemplateId)]);
  return res;
}

export async function listReportSnapshots(assessmentId: AssessmentId, reportTemplateId: TprmReportTemplateId) {
  return await platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/assessments/{assessment_id}/report/{report_template_id}/snapshots",
    {path: {assessment_id: assessmentId, report_template_id: reportTemplateId}},
  );
}

export async function setSnapshotPublished(snapshotId: PortalReportSnapshotId, published: boolean) {
  await platformedClient.internalRequest(
    "patch",
    "/region/{region}/api/tprm/report_snapshots/{portal_report_snapshot_id}/published",
    {path: {portal_report_snapshot_id: snapshotId}, body: published},
  );
  await invalidateQueries([q.tprm.reportSnapshots.filter()]);
}

export async function deleteSnapshot(snapshotId: PortalReportSnapshotId) {
  await platformedClient.internalRequest(
    "delete",
    "/region/{region}/api/tprm/report_snapshots/{portal_report_snapshot_id}",
    {path: {portal_report_snapshot_id: snapshotId}},
  );
  await invalidateQueries([q.tprm.reportSnapshots.filter()]);
}

export const exportAssessment = async (assessmentId: AssessmentId) => {
  const res = platformedClient.internalRequest("post", "/region/{region}/api/tprm/assessments/{assessment_id}/export", {
    path: {assessment_id: assessmentId},
  });
  return res;
};
